import * as React from 'react';
import {
  Box,
  Typography,
  Paper,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  LayoutGrid,
  List,
  GripVertical,
  PlayCircle,
  Sun,
  Moon,
  Eye,
  EyeOff,
  Rows,
  ChevronDown,
  ChevronUp,
  Layout,
  FileText,
} from 'lucide-mui';
import useTags from '../../hooks/useTags';
import { useTheme as useCustomTheme } from '../../theme/ThemeContext';
import { ToggleSetting, SettingsSection } from './common';
import {
  VIDEO_LEFT_COLUMN_WIDTH_KEY,
  LIBRARY_VIEW_TYPE_KEY,
  CHAPTERS_TIMESTAMPS_VISIBLE_KEY,
  CHAPTERS_EXPANDED_KEY,
  VIDEO_CHAPTERS_VISIBLE_KEY,
  NOTEPAD_VISIBLE_KEY,
} from '../../constants/tags';

interface VideoPreviewProps {
  leftColumnWidth: number;
}

const VideoPreview: React.FC<VideoPreviewProps> = ({ leftColumnWidth }) => {
  const [isDragging, setIsDragging] = React.useState(false);
  const [localWidth, setLocalWidth] = React.useState(leftColumnWidth);
  const [dragStartX, setDragStartX] = React.useState(0);
  const [initialWidth, setInitialWidth] = React.useState(leftColumnWidth);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const { setTag } = useTags();
  const { isDarkMode } = useCustomTheme();

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStartX(e.clientX);
    setInitialWidth(localWidth);
    e.preventDefault();
  };

  const handleMouseMove = React.useCallback(
    (e: MouseEvent) => {
      if (!isDragging || !containerRef.current) return;

      const container = containerRef.current;
      const rect = container.getBoundingClientRect();
      const dragDistance = e.clientX - dragStartX;
      const dragPercentage = (dragDistance / rect.width) * 100;
      const newWidth = Math.max(
        50,
        Math.min(75, initialWidth + dragPercentage)
      );
      setLocalWidth(newWidth);
    },
    [isDragging, dragStartX, initialWidth]
  );

  const handleMouseUp = React.useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      setTag({ key: VIDEO_LEFT_COLUMN_WIDTH_KEY, value: String(localWidth) });
    }
  }, [isDragging, localWidth, setTag]);

  React.useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  React.useEffect(() => {
    if (!isDragging) {
      setLocalWidth(leftColumnWidth);
    }
  }, [leftColumnWidth, isDragging]);

  return (
    <Paper
      ref={containerRef}
      elevation={0}
      sx={{
        width: '100%',
        height: 200,
        mb: 3,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        position: 'relative',
        display: 'flex',
        cursor: isDragging ? 'col-resize' : 'default',
        bgcolor: 'transparent',
      }}
    >
      {/* Video and Tabs Column */}
      <Box
        sx={{
          width: `${localWidth}%`,
          height: '100%',
          bgcolor: 'background.paper',
          borderRight: '1px solid',
          borderColor: 'divider',
          transition: isDragging ? 'none' : 'width 0.2s',
          display: 'flex',
          flexDirection: 'column',
        }}
        data-testid="video-preview-left-column"
      >
        {/* Video Area */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: isDarkMode ? 'action.selected' : 'action.hover',
            position: 'relative',
          }}
        >
          <PlayCircle
            sx={{
              color: 'text.primary',
              fontSize: '24px !important',
              opacity: 0.5,
            }}
          />
        </Box>

        {/* Tabs Area */}
        <Box
          sx={{
            height: '40px',
            borderTop: '1px solid',
            borderColor: 'divider',
            p: 1,
          }}
        >
          <Box
            sx={{
              height: 8,
              bgcolor: isDarkMode ? 'action.selected' : 'action.hover',
              borderRadius: 0.5,
              width: '100%',
            }}
          />
        </Box>
      </Box>

      {/* Drag Handle */}
      <Box
        onMouseDown={handleMouseDown}
        sx={{
          position: 'absolute',
          left: `${localWidth}%`,
          top: 0,
          width: '20px',
          height: '100%',
          transform: 'translateX(-50%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1,
          cursor: 'col-resize',
          color: isDragging ? 'primary.main' : 'text.secondary',
          '&:hover': {
            color: 'primary.main',
          },
        }}
        data-testid="video-preview-drag-handle"
      >
        <GripVertical
          sx={{
            color: 'text.primary',
            fontSize: '16px !important',
          }}
        />
        {isDragging && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              py: 0.5,
              px: 1,
              borderRadius: 1,
              fontSize: '0.75rem',
              fontWeight: 500,
              whiteSpace: 'nowrap',
            }}
          >
            {Math.round(localWidth)}%
          </Box>
        )}
      </Box>

      {/* Ask AI Column */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
          p: 2,
          gap: 1,
        }}
      >
        <Box
          sx={{
            height: 8,
            bgcolor: isDarkMode ? 'action.selected' : 'action.hover',
            borderRadius: 0.5,
            width: '60%',
          }}
        />
        <Box
          sx={{
            height: 8,
            bgcolor: isDarkMode ? 'action.selected' : 'action.hover',
            borderRadius: 0.5,
            width: '80%',
          }}
        />
        <Box
          sx={{
            height: 8,
            bgcolor: isDarkMode ? 'action.selected' : 'action.hover',
            borderRadius: 0.5,
            width: '40%',
          }}
        />
      </Box>
    </Paper>
  );
};

export default function DisplaySettings() {
  const { getTag, setTag } = useTags();
  const { isDarkMode, toggleTheme } = useCustomTheme();

  const handleToggleChange = (key: string) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (key === 'darkMode') {
      toggleTheme();
    } else {
      setTag({ key, value: event.target.checked ? 'true' : 'false' });
    }
  };

  const leftColumnWidth = parseInt(
    getTag(VIDEO_LEFT_COLUMN_WIDTH_KEY) || '300',
    10
  );
  const currentViewType = getTag(LIBRARY_VIEW_TYPE_KEY) || 'grid';
  const isTimestampsVisible =
    getTag(CHAPTERS_TIMESTAMPS_VISIBLE_KEY) === 'true';
  const isNotepadVisible = getTag(NOTEPAD_VISIBLE_KEY) === 'true';
  const isChaptersVisible = getTag(VIDEO_CHAPTERS_VISIBLE_KEY) === 'true';
  const isChaptersExpanded = getTag(CHAPTERS_EXPANDED_KEY) === 'true';

  return (
    <Box
      sx={{
        flexGrow: 1,
        height: '100%',
        overflow: 'auto',
        bgcolor: 'transparent',
        px: 3,
        pt: 1,
        pb: 3,
        '& > *:not(:last-child)': {
          mb: 3,
        },
      }}
    >
      <SettingsSection title="Theme">
        <ToggleSetting
          checked={isDarkMode}
          onChange={handleToggleChange('darkMode')}
          label="Dark Mode"
          description="Switch between light and dark theme for the entire application"
          icon={
            isDarkMode ? (
              <Moon
                sx={{ color: 'text.primary', fontSize: '20px !important' }}
              />
            ) : (
              <Sun
                sx={{ color: 'text.primary', fontSize: '20px !important' }}
              />
            )
          }
        />
      </SettingsSection>

      <SettingsSection title="Library View">
        <Box sx={{ py: 2 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 2, sm: 0 },
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ flex: 1, mr: { xs: 0, sm: 3 }, width: '100%' }}>
              <Box sx={{ display: 'flex' }}>
                <Box
                  sx={{
                    color: 'text.secondary',
                    display: 'flex',
                    alignItems: 'center',
                    alignSelf: 'center',
                    mr: 2.5,
                    height: '42px',
                    minWidth: '22px',
                  }}
                >
                  <Layout
                    sx={{ color: 'text.primary', fontSize: '20px !important' }}
                  />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    View Type
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    Choose how your recordings are displayed in the library
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: { xs: 'flex-end', sm: 'flex-start' },
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              <ToggleButtonGroup
                value={currentViewType}
                exclusive
                onChange={(e, newValue) => {
                  if (newValue !== null) {
                    setTag({ key: LIBRARY_VIEW_TYPE_KEY, value: newValue });
                  }
                }}
                color="secondary"
                size="small"
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                {['grid', 'list'].map((viewType) => (
                  <ToggleButton
                    key={viewType}
                    value={viewType}
                    sx={{
                      flex: { xs: 1, sm: 'none' },
                      px: 2,
                      py: 1,
                      textTransform: 'none',
                      '&.Mui-selected': {
                        bgcolor: 'action.selected',
                        color: 'text.primary',
                        '&:hover': {
                          bgcolor: 'action.selected',
                          color: 'text.primary',
                        },
                      },
                    }}
                  >
                    {viewType === 'grid' ? (
                      <LayoutGrid
                        sx={{
                          color: 'text.primary',
                          fontSize: '20px !important',
                        }}
                      />
                    ) : (
                      <List
                        sx={{
                          color: 'text.primary',
                          fontSize: '20px !important',
                        }}
                      />
                    )}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
          </Box>
        </Box>
      </SettingsSection>

      <SettingsSection title="Video Player">
        <Box sx={{ py: 2 }}>
          <VideoPreview leftColumnWidth={leftColumnWidth} />
        </Box>

        <Box>
          <ToggleSetting
            checked={isChaptersVisible}
            onChange={handleToggleChange(VIDEO_CHAPTERS_VISIBLE_KEY)}
            label="Show Chapters"
            description="Show or hide the chapters panel in the video player"
            icon={
              isChaptersVisible ? (
                <Rows
                  sx={{ color: 'text.primary', fontSize: '20px !important' }}
                />
              ) : (
                <Rows
                  sx={{ color: 'text.primary', fontSize: '20px !important' }}
                />
              )
            }
          />

          <ToggleSetting
            checked={isChaptersExpanded}
            onChange={handleToggleChange(CHAPTERS_EXPANDED_KEY)}
            label="Expand Chapters"
            description="Automatically expand chapter details"
            icon={
              isChaptersExpanded ? (
                <ChevronUp
                  sx={{ color: 'text.primary', fontSize: '20px !important' }}
                />
              ) : (
                <ChevronDown
                  sx={{ color: 'text.primary', fontSize: '20px !important' }}
                />
              )
            }
          />

          <ToggleSetting
            checked={isTimestampsVisible}
            onChange={handleToggleChange(CHAPTERS_TIMESTAMPS_VISIBLE_KEY)}
            label="Show Chapter Timestamps"
            description="Show timestamps in the chapters panel"
            icon={
              isTimestampsVisible ? (
                <Eye
                  sx={{ color: 'text.primary', fontSize: '20px !important' }}
                />
              ) : (
                <EyeOff
                  sx={{ color: 'text.primary', fontSize: '20px !important' }}
                />
              )
            }
          />
        </Box>
      </SettingsSection>

      <SettingsSection title="Recorder View">
        <ToggleSetting
          checked={isNotepadVisible}
          onChange={handleToggleChange(NOTEPAD_VISIBLE_KEY)}
          label="Show Notepad"
          description="Show or hide the notepad feature"
          icon={
            isNotepadVisible ? (
              <FileText
                sx={{ color: 'text.primary', fontSize: '20px !important' }}
              />
            ) : (
              <FileText
                sx={{ color: 'text.primary', fontSize: '20px !important' }}
              />
            )
          }
        />
      </SettingsSection>
    </Box>
  );
}
